export interface RouteEndpoints {
  from: string;
  to: string;
  /**
   * Where each endpoint is, when the rider chose it from the suggestion list
   * rather than typing it freehand. The suggestion already carried its centre,
   * so passing it on saves the map a geocoding round-trip — and guarantees the
   * pin lands on the row that was clicked rather than on whatever the geocoder
   * makes of the text afterwards.
   */
  fromCoords?: [number, number];
  toCoords?: [number, number];
}

const sameCoords = (a?: [number, number], b?: [number, number]) =>
  a === b || (!!a && !!b && a[0] === b[0] && a[1] === b[1]);

/**
 * Whether two endpoint pairs describe the same trip.
 *
 * Used by the ride forms to keep a stable object identity across renders —
 * `endpoints` sits in MapPreview's route effect deps, and a fresh object every
 * render would re-issue the route request in a loop. Coordinates are part of
 * the comparison because the same two strings can arrive with and without
 * them, and that difference changes whether the map has to geocode.
 */
export const sameEndpoints = (
  a: RouteEndpoints | null,
  b: RouteEndpoints | null,
): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.from === b.from &&
    a.to === b.to &&
    sameCoords(a.fromCoords, b.fromCoords) &&
    sameCoords(a.toCoords, b.toCoords));
